<template>
  <AIVDialog
    v-model="isVisible"
    title="Report New Incident"
    width="90%"
    height="85vh"
    class="incident-modal"
  >
    <form @submit.prevent="saveIncident">
      <div class="row">
        <div class="col-md-12 mb-3">
          <label class="form-label">Incident Title *</label>
          <el-input
            v-model="incident.title"
            placeholder="Brief description of the incident"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">Priority *</label>
          <AIVSelect
            v-model="incident.priority"
            :options="priorityOptions"
            placeholder="Select Priority"
            :searchable="false"
            :clearable="false"
          />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">Category *</label>
          <AIVSelect
            v-model="incident.category"
            :options="categoryOptions"
            placeholder="Select Category"
            :searchable="false"
            :clearable="false"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">Date of Incident *</label>
          <el-input
            type="date"
            v-model="incident.incident_date"
            placeholder="Date"
            class="w-100"
          />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">Time of Incident</label>
          <AIVTimeSelect
            v-model="incident.incident_time"
            :allowExactMinutes="true"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">Client Involved</label>
          <AIVSelect
            v-model="incident.client_id"
            :options="clientOptions"
            placeholder="Select Client"
          />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">Caregiver Involved</label>
          <AIVSelect
            v-model="incident.caregiver_id"
            :options="caregiverOptions"
            placeholder="Select Caregiver"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 mb-3">
          <label class="form-label">Location</label>
          <el-input
            v-model="incident.location"
            placeholder="Where did the incident occur?"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 mb-3">
          <label class="form-label">Description *</label>
          <el-input
            type="textarea"
            v-model="incident.description"
            :rows="4"
            placeholder="Detailed description of what happened..."
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 mb-3">
          <label class="form-label">Immediate Actions Taken</label>
          <el-input
            type="textarea"
            v-model="incident.actions_taken"
            :rows="3"
            placeholder="What immediate actions were taken?"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 mb-3">
          <el-checkbox v-model="incident.requires_follow_up">
            This incident requires follow-up action
          </el-checkbox>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 mb-3">
          <label class="form-label">Additional Email Recipients</label>
          <div class="alert alert-info" style="background: rgba(23, 162, 184, 0.15); border: 1px solid rgba(23, 162, 184, 0.4); border-radius: 6px; padding: 8px 12px; color: #17a2b8; font-size: 13px; margin-bottom: 8px;">
            <i class="fa-solid fa-info-circle mr-1"></i>
            H Northcutt and Hailey Lindberg will automatically receive an email for every incident report.<template v-if="clientAdmin">
              <br>
              <i class="fa-solid fa-info-circle mr-1"></i>
              {{ clientAdmin.first_name }} {{ clientAdmin.last_name }} will also be emailed as the admin assigned to this client.
            </template>
          </div>
          <AIVSelect
            v-model="incident.additional_recipients"
            :options="adminRecipientOptions"
            placeholder="Select additional admins to notify..."
            :multi-select="true"
          />
        </div>
      </div>
    </form>

    <template slot="footer">
      <div class="text-center">
        <button
          @click="saveIncident"
          :disabled="!isFormValid || submitting"
          class="btn btn-primary"
        >
          <i :class="submitting ? 'fa fa-spinner fa-spin' : 'fa fa-save'"></i> {{ submitting ? 'Reporting...' : 'Report Incident' }}
        </button>
        <button 
          @click="closeModal" 
          class="btn btn-secondary ml-2"
        >
          Cancel
        </button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { incidentsService } from "@/serviceHandlers/incidentsService";
import { localDateString } from "@/utils/dateOnly";
import { adminUsersService } from "@/serviceHandlers/adminUsersService";

export default {
  name: "IncidentModal",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    clients: {
      type: Array,
      default: () => []
    },
    caregivers: {
      type: Array,
      default: () => []
    },
    shiftId: {
      type: [Number, String],
      default: null
    },
    prefillClientId: {
      type: [Number, String],
      default: null
    },
    prefillCaregiverId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      submitting: false,
      adminList: [],
      clientAdmin: null,
      incident: {
        title: '',
        priority: null,
        category: null,
        incident_date: localDateString(), // local today — UTC date is tomorrow after ~6pm Denver
        incident_time: '',
        client_id: null,
        caregiver_id: null,
        location: '',
        description: '',
        actions_taken: '',
        requires_follow_up: false,
        additional_recipients: [],
        status: 'Open'
      }
    };
  },
  watch: {
    value(val) {
      if (val) {
        if (this.prefillClientId) this.incident.client_id = this.prefillClientId;
        if (this.prefillCaregiverId) this.incident.caregiver_id = this.prefillCaregiverId;
        this.loadAdmins();
      }
    },
    "incident.client_id"(newId) {
      this.loadClientAdmin(newId);
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    priorityOptions() {
      return ['Low', 'Medium', 'High', 'Critical'].map(p => ({ id: p, name: p }));
    },
    categoryOptions() {
      return ['Medical', 'Safety', 'Equipment', 'Behavioral', 'Medication', 'Fall', 'Other'].map(c => ({ id: c, name: c }));
    },
    clientOptions() {
      return this.clients.map(c => ({ id: c.id, name: `${c.first_name} ${c.last_name}` }));
    },
    caregiverOptions() {
      return this.caregivers.map(c => ({ id: c.id, name: `${c.first_name} ${c.last_name}` }));
    },
    adminRecipientOptions() {
      const excludeEmails = ['hnorthcutt@bexiam.com', 'hlindberg@theoppo.com'];
      return this.adminList
        .filter(a => !excludeEmails.includes((a.email || '').toLowerCase()))
        .map(a => ({ id: a.id, name: `${a.first_name} ${a.last_name}` }));
    },
    isFormValid() {
      return this.incident.title && 
             this.incident.priority && 
             this.incident.category && 
             this.incident.incident_date && 
             this.incident.description;
    }
  },
  methods: {
    async saveIncident() {
      if (!this.isFormValid || this.submitting) return;
      this.submitting = true;

      try {
        const loginStore = require("@/stores/loginStore").useLoginStore();
        const incidentData = {
          ...this.incident,
          reported_by: loginStore.user?.id,
        };

        if (this.shiftId) {
          incidentData.shift_id = this.shiftId;
        }

        await incidentsService.create(incidentData);
        this.$notifySuccess("Incident reported successfully. All administrators have been notified.");
        this.$emit('saved', incidentData);
        this.closeModal();
      } catch (error) {
        this.$notifyError("Error saving incident: " + (error.message || error));
      } finally {
        this.submitting = false;
      }
    },

    closeModal() {
      this.isVisible = false;
      this.resetForm();
    },

    async loadAdmins() {
      try {
        const response = await adminUsersService.list();
        this.adminList = response || [];
      } catch (e) {
        this.adminList = [];
      }
    },

    async loadClientAdmin(clientId) {
      if (!clientId) {
        this.clientAdmin = null;
        return;
      }
      try {
        const response = await incidentsService.getClientAdmin(clientId);
        this.clientAdmin = response?.admin || null;
      } catch (e) {
        this.clientAdmin = null;
      }
    },

    resetForm() {
      this.clientAdmin = null;
      this.incident = {
        title: '',
        priority: null,
        category: null,
        incident_date: localDateString(), // local today — UTC date is tomorrow after ~6pm Denver
        incident_time: '',
        client_id: null,
        caregiver_id: null,
        location: '',
        description: '',
        actions_taken: '',
        requires_follow_up: false,
        additional_recipients: [],
        status: 'Open'
      };
    }
  }
};
</script>

<style scoped>
.form-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

:deep(.el-input__inner) {
  background-color: #656565;
  border: 1px solid #bdbdbd;
  color: #e0e0e0;
  border-radius: 4px;
  height: 40px;
  font-size: 14px;
}

:deep(.el-input__inner::placeholder) {
  color: #9b9b9b;
}

:deep(.el-input__inner:focus) {
  border-color: #ffffff;
}

:deep(.el-textarea__inner) {
  background-color: #656565;
  border: 1px solid #bdbdbd;
  color: #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
}

:deep(.el-textarea__inner::placeholder) {
  color: #9b9b9b;
}

:deep(.el-textarea__inner:focus) {
  border-color: #ffffff;
}

:deep(.el-checkbox__label) {
  color: #e0e0e0 !important;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>